import { useEffect, useState } from 'react'
import type { ActionStatus } from './types'

/** Estado dos filtros das telas de Ações (abertas / por semana). */
export interface ActionsFiltersState {
  status: ActionStatus | 'all'
  week: string
  reviewWeek: string
  clientId: string
}

/** Padrão: só Pendentes, sem recorte de cliente nem de data. */
export const DEFAULT_ACTIONS_FILTERS: ActionsFiltersState = {
  status: 'pending',
  week: '',
  reviewWeek: '',
  clientId: '',
}

export function isDefaultActionsFilters(f: ActionsFiltersState): boolean {
  return (
    f.status === DEFAULT_ACTIONS_FILTERS.status &&
    f.week === DEFAULT_ACTIONS_FILTERS.week &&
    f.reviewWeek === DEFAULT_ACTIONS_FILTERS.reviewWeek &&
    f.clientId === DEFAULT_ACTIONS_FILTERS.clientId
  )
}

function load(key: string): ActionsFiltersState {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return { ...DEFAULT_ACTIONS_FILTERS }
    const p = JSON.parse(raw) as Partial<ActionsFiltersState>
    return {
      status: p.status ?? DEFAULT_ACTIONS_FILTERS.status,
      week: p.week ?? '',
      reviewWeek: p.reviewWeek ?? '',
      clientId: p.clientId ?? '',
    }
  } catch {
    return { ...DEFAULT_ACTIONS_FILTERS }
  }
}

/**
 * Filtros de Ações com persistência em localStorage — sobrevivem a navegar para
 * um relatório e voltar, ou a um reload. A chave é compartilhada entre as telas
 * "Ações abertas" e "Ações por semana" (mesmos dados, só muda o agrupamento).
 */
export function useActionsFilters(storageKey = 'actions-filters') {
  const [filters, setFilters] = useState<ActionsFiltersState>(() => load(storageKey))

  useEffect(() => {
    try {
      if (isDefaultActionsFilters(filters)) localStorage.removeItem(storageKey)
      else localStorage.setItem(storageKey, JSON.stringify(filters))
    } catch {
      /* localStorage indisponível — segue sem persistir */
    }
  }, [filters, storageKey])

  const set = <K extends keyof ActionsFiltersState>(key: K, value: ActionsFiltersState[K]) =>
    setFilters((prev) => ({ ...prev, [key]: value }))

  return {
    filters,
    set,
    isDefault: isDefaultActionsFilters(filters),
    reset: () => setFilters({ ...DEFAULT_ACTIONS_FILTERS }),
  }
}
